/**
 * SetrixServerSettingsCtrl controller
 * Manages Setrix server connections for transcoder integration
 */

/* @ngInject */
export default function SetrixServerSettingsCtrl ($scope, $timeout, $q, SetrixServer, alertLiteSvc, authSvc) {
  const vm = this

  const parentVm = $scope?.$parent?.vm

  let networkId = parentVm?.pckg?.id ?? $scope?.networkId ?? authSvc?.getUser?.()?.network?.id
  let multiorgEnabled = !!(parentVm?.showMultitenantTabs ?? $scope?.showMultitenantTabs)

  if (multiorgEnabled && networkId == null) {
    const userNetworkId = authSvc?.getUser?.()?.network?.id
    if (userNetworkId != null) {
      networkId = userNetworkId
    } else {
      multiorgEnabled = false
    }
  }

  const BADGE_MAP = {
    VERIFIED: { text: 'Verified', cssClass: 'verified' },
    VERIFICATION_ERROR: { text: 'Invalid', cssClass: 'inactive' },
    UNVERIFIED: { text: 'N/A', cssClass: 'na' }
  }

  vm.networkId = networkId
  vm.multiorgEnabled = multiorgEnabled

  vm.servers = []
  vm.deletedServerIds = []
  vm.saving = false

  SetrixServer.loadAll(networkId, multiorgEnabled)
    .then(res => {
      vm.servers = (res.data.payload || []).map(s => ({
        ...s,
        maskedKey: s.maskedKey || '',
        _status: s.status,
        _baselineAddress: s.address
      }))
    })
    .catch(rejection => alertLiteSvc.alertError(rejection?.statusText))

  vm.addServer = function () {
    vm.servers.unshift({ name: '', address: '', apiKey: '', _status: 'UNVERIFIED', isOpen: true })
  }

  vm.markUnverified = function (server) {
    if (!server || server._verifySpinner) return
    server._status = 'UNVERIFIED'
  }

  function hasApiKeyValue (server) {
    const v = server && server.apiKey
    return v != null && String(v).trim() !== ''
  }

  /**
   * Re-run API Token validation after address edit so required/masked state updates and errors show.
   */
  vm.onAddressChange = function (server, index) {
    vm.markUnverified(server)
    if (!server || server._verifySpinner) return

    $timeout(() => {
      const form = $scope.setrixForm
      if (!form) return
      const apiKeyField = form['serverApiKey_' + index]
      if (!apiKeyField) return

      apiKeyField.$validate()

      if (!server.id) return

      const tokenRequired = server.address !== server._baselineAddress
      if (tokenRequired && !hasApiKeyValue(server)) {
        apiKeyField.$setTouched()
      } else if (!tokenRequired) {
        apiKeyField.$setUntouched()
      }
    }, 0)
  }

  vm.onServerFieldChange = function (fieldName) {
    if (!fieldName) return
    const match = String(fieldName).match(/_(\d+)$/)
    if (!match) return
    const idx = Number(match[1])
    if (Number.isNaN(idx)) return
    vm.markUnverified(vm.servers[idx])
  }

  function verifyOneServer (server) {
    server._verifySpinner = true
    return SetrixServer.verify(networkId, { name: server.name, address: server.address, apiKey: server.apiKey }, multiorgEnabled)
      .then(res => {
        server._status = res.data.payload.status
        return server._status
      })
      .catch(() => {
        server._status = 'VERIFICATION_ERROR'
        return server._status
      })
      .finally(() => {
        server._verifySpinner = false
      })
  }

  vm.verify = function (server) {
    verifyOneServer(server)
  }

  vm.delete = function (server, $index) {
    if (server.id) {
      vm.deletedServerIds.push(server.id)
      vm.servers.splice($index, 1)
    } else {
      vm.servers.splice($index, 1)
    }
  }

  vm.save = function (form) {
    const create = vm.servers
      .filter(s => !s.id)
      .map(s => ({
        name: s.name,
        address: s.address,
        apiKey: s.apiKey || undefined,
        status: s._status
      }))

    const update = vm.servers
      .filter(s => !!s.id)
      .map(s => ({
        id: s.id,
        name: s.name,
        address: s.address,
        apiKey: s.apiKey || undefined,
        status: s._status
      }))

    const del = Array.from(new Set(vm.deletedServerIds)).filter(id => id != null)
    const serversNeedingVerify = vm.servers.filter(s => s._status === 'UNVERIFIED')

    vm.saving = true

    const afterVerifications = serversNeedingVerify.reduce(
      (promise, server) => promise.then(() =>
        verifyOneServer(server).then(status => {
          if (status !== 'VERIFIED') {
            return $q.reject({ code: 'VERIFY_FAILED', server, status })
          }
        })
      ),
      $q.resolve()
    )

    afterVerifications
      .then(() => SetrixServer.saveAll(networkId, { create, update, delete: del }, multiorgEnabled))
      .then(res => {
        vm.servers = (res.data.payload || []).map(s => ({
          ...s,
          maskedKey: s.maskedKey || '',
          _status: s.status,
          _baselineAddress: s.address
        }))
        alertLiteSvc.alert('Setrix Server Settings updated', 'success')
        vm.deletedServerIds = []
        form.$setPristine()
        form.$setUntouched()
      })
      .catch(rejection => {
        if (rejection && rejection.code === 'VERIFY_FAILED') {
          const label = (rejection.server && rejection.server.name) ? rejection.server.name : 'Server'
          alertLiteSvc.alertError(label + ' verification failed. Update was not saved.')
        } else {
          alertLiteSvc.alertError(rejection?.statusText)
        }
      })
      .finally(() => {
        vm.saving = false
      })
  }

  vm.verifyEnabled = function (server) {
    return !!(server.name && server.address && server.apiKey && !server._verifySpinner && !vm.saving)
  }

  vm.badge = function (server) {
    return BADGE_MAP[server._status] || BADGE_MAP.UNVERIFIED
  }

  vm.hasInvalidServer = function () {
    return vm.servers.some(s => s._status === 'VERIFICATION_ERROR')
  }
}
